// Collections workspace — publisher-facing collection CRUD (plan §5; /pub/collections).
// Publishers (and platform admins) create / rename / delete the collections of a
// category they administer; the backend is publisher-scoped, so a [P] only ever
// sees and touches their own categories. Granting users to a collection is done
// from the Users page (Manage grants); this screen owns the collection objects
// themselves — the unit a folder/sync drop is later bound to (§7d).
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { FolderTree, Plus, X, Trash2, Pencil, Lock } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api-client';
import { AppShell } from '@/components/AppShell';

interface PublisherOption {
  id: string;
  slug: string;
  name: string;
}

interface Collection {
  id: string;
  publisherId: string;
  slug: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  documentCount: number;
}

export function Collections() {
  const { t } = useTranslation('collections');

  // Categories the caller may publish for — server-resolved ([P]: own; [A]: all).
  const [publishers, setPublishers] = useState<PublisherOption[] | null>(null);
  const [publisherId, setPublisherId] = useState('');
  const [collections, setCollections] = useState<Collection[] | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Collection | null>(null);

  useEffect(() => {
    apiFetch<{ data: PublisherOption[] }>('/pub/publishers')
      .then((res) => {
        setPublishers(res.data);
        setPublisherId((prev) => prev || res.data[0]?.id || '');
      })
      .catch(() => setPublishers([]));
  }, []);

  const load = useCallback(async () => {
    if (!publisherId) return;
    setCollections(null);
    try {
      const res = await apiFetch<{ data: Collection[] }>(`/pub/collections?publisherId=${publisherId}`);
      setCollections(res.data);
    } catch {
      setCollections([]);
    }
  }, [publisherId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(c: Collection) {
    if (!window.confirm(t('confirmDelete', { name: c.name }))) return;
    try {
      await apiFetch(`/pub/collections/${c.id}`, { method: 'DELETE' });
      toast.success(t('toast.deleted'));
      void load();
    } catch (err) {
      // 409 = the collection still has documents → localized guidance.
      toast.error(
        err instanceof ApiError && err.status === 409
          ? t('hasDocuments')
          : err instanceof ApiError
            ? err.message
            : t('common:error'),
      );
    }
  }

  if (publishers === null) {
    return (
      <AppShell>
        <div className="flex justify-center py-24">
          <div className="w-9 h-9 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
        </div>
      </AppShell>
    );
  }

  if (publishers.length === 0) {
    return (
      <AppShell>
        <div className="text-center py-24 animate-fade-up">
          <p className="text-sm text-slate-500 max-w-md mx-auto">{t('noCategory')}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-10 animate-fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-medium tracking-tight gradient-text pb-1">{t('title')}</h1>
            <p className="text-sm font-light text-slate-500 mt-1 max-w-xl">{t('subtitle')}</p>
          </div>
          <div className="flex items-end gap-3">
            {publishers.length > 1 && (
              <label className="text-xs font-semibold text-slate-500 flex flex-col gap-1.5">
                {t('categoryLabel')}
                <select value={publisherId} onChange={(e) => setPublisherId(e.target.value)} className="paper-input w-52">
                  {publishers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button type="button" onClick={() => setCreateOpen(true)} className="btn-gradient">
              <Plus className="w-4 h-4" />
              {t('new')}
            </button>
          </div>
        </div>
      </div>

      {collections === null ? (
        <div className="flex justify-center py-24">
          <div className="w-9 h-9 rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
        </div>
      ) : collections.length === 0 ? (
        <div className="text-center py-24 animate-fade-up">
          <div className="inline-flex p-[2px] rounded-2xl bg-brand-gradient mb-5">
            <div className="p-4 rounded-2xl bg-white">
              <FolderTree className="w-8 h-8 text-brand-purple" />
            </div>
          </div>
          <p className="text-sm font-light text-slate-500 max-w-sm mx-auto">{t('empty')}</p>
        </div>
      ) : (
        <div className="space-y-2 animate-fade-up [animation-delay:0.08s]">
          {collections.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between gap-4 rounded-xl bg-white border border-slate-200/80 shadow-card px-5 py-4 hover:border-brand-purple/30 transition-colors"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <FolderTree className="w-4 h-4 text-brand-purple/70 shrink-0" />
                  <p className="text-sm font-semibold text-ink truncate">
                    {c.isDefault ? t('common:uncategorized') : c.name}
                  </p>
                  {c.isDefault && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500">
                      <Lock className="w-2.5 h-2.5" />
                      {t('systemBadge')}
                    </span>
                  )}
                </div>
                <p className="text-xs font-light text-slate-400 mt-1">
                  <code className="font-mono">{c.slug}</code> · {t('documentsCount', { count: c.documentCount })}
                  {c.description ? <span className="text-slate-400"> · {c.description}</span> : null}
                </p>
              </div>
              {c.isDefault ? (
                <span className="text-xs font-light text-slate-300 shrink-0" title={t('uncategorizedHint')}>
                  —
                </span>
              ) : (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => setEditTarget(c)}
                    title={t('edit')}
                    className="p-2 rounded-md text-slate-400 hover:text-brand-purple hover:bg-brand-purple/5 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void remove(c)}
                    title={t('delete')}
                    className="p-2 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <CollectionDialog
        mode={editTarget ? 'edit' : createOpen ? 'create' : null}
        target={editTarget}
        publisherId={publisherId}
        onClose={() => {
          setCreateOpen(false);
          setEditTarget(null);
        }}
        onDone={() => void load()}
      />
    </AppShell>
  );
}

// ── Create / edit dialog ──────────────────────────────────────

function CollectionDialog({
  mode,
  target,
  publisherId,
  onClose,
  onDone,
}: {
  mode: 'create' | 'edit' | null;
  target: Collection | null;
  publisherId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('collections');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && target) {
      setName(target.name);
      setDescription(target.description ?? '');
    } else if (mode === 'create') {
      setName('');
      setSlug('');
      setDescription('');
    }
  }, [mode, target]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (saving || !name.trim()) return;
    setSaving(true);
    try {
      if (mode === 'edit' && target) {
        await apiFetch(`/pub/collections/${target.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ name: name.trim(), description: description.trim() || null }),
        });
        toast.success(t('toast.updated'));
      } else {
        await apiFetch('/pub/collections', {
          method: 'POST',
          body: JSON.stringify({
            publisherId,
            name: name.trim(),
            ...(slug.trim() ? { slug: slug.trim() } : {}),
            ...(description.trim() ? { description: description.trim() } : {}),
          }),
        });
        toast.success(t('toast.created', { name: name.trim() }));
      }
      onClose();
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('common:error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={mode !== null} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-md rounded-2xl bg-white shadow-2xl p-6 animate-dialog-in focus:outline-none max-h-[90dvh] overflow-y-auto custom-scrollbar">
          <div className="flex items-start justify-between gap-4 mb-5">
            <Dialog.Title className="text-base font-semibold text-ink flex items-center gap-2">
              <FolderTree className="w-4 h-4 text-brand-purple" />
              {mode === 'edit' ? t('dialog.editTitle') : t('dialog.createTitle')}
            </Dialog.Title>
            <Dialog.Close className="p-1 rounded-md text-slate-400 hover:text-ink hover:bg-slate-100 transition-colors">
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>
          <form onSubmit={(e) => void submit(e)} className="space-y-4">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.name')}</span>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('dialog.namePlaceholder')}
                className="paper-input"
                autoFocus
              />
            </label>
            {mode === 'create' && (
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.slug')}</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="circet"
                  className="paper-input font-mono"
                />
                <span className="block text-[11px] font-light text-slate-400 mt-1.5 leading-snug">{t('dialog.slugHint')}</span>
              </label>
            )}
            <label className="block">
              <span className="block text-xs font-semibold text-slate-500 mb-1.5">{t('dialog.description')}</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('dialog.descriptionPlaceholder')}
                rows={2}
                className="paper-input resize-none"
              />
            </label>
            <div className="flex justify-end gap-3 pt-1">
              <Dialog.Close type="button" className="btn-quiet">
                {t('common:cancel')}
              </Dialog.Close>
              <button type="submit" disabled={saving || !name.trim()} className="btn-gradient">
                {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {mode === 'edit' ? (saving ? t('dialog.saving') : t('dialog.save')) : saving ? t('dialog.creating') : t('dialog.create')}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
